import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Search, Loader2, X, Plus, CheckCircle,
  BarChart3, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const FirstMealScreen = () => {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  const searchInputRef = useRef(null);

  // Search foods with debounce
  useEffect(() => {
    const searchFoods = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        const response = await fetch(`${API}/foods?search=${encodeURIComponent(searchQuery)}`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
        }
      } catch (error) {
        console.error("Search error:", error);
      }
    };

    const debounce = setTimeout(searchFoods, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, token]);

  const toggleFoodSelection = (food) => {
    setSelectedFoods((prev) => {
      const exists = prev.find((f) => f.food_id === food.food_id);
      if (exists) {
        return prev.filter((f) => f.food_id !== food.food_id);
      }
      return [...prev, food];
    });
    setSearchQuery("");
    setSearchResults([]);
  };

  const analyzeFirstMeal = async () => {
    if (selectedFoods.length === 0) {
      toast.error("Please select at least one food");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/meals/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({ 
          food_ids: selectedFoods.map(f => f.food_id)
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysisResult(data);
        // Refresh user data to update meal_analysis_count
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        toast.error("Failed to analyze meal");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze meal");
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToDashboard = () => {
    navigate("/dashboard");
  };

  const getGIColor = (impact) => {
    if (impact === "Low") return "text-success bg-success/10";
    if (impact === "Medium") return "text-warning bg-warning/10";
    return "text-destructive bg-destructive/10";
  };

  // Show success state after analysis
  if (analysisResult) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-lg rounded-3xl border border-orange-100/50 shadow-soft-lift p-8 space-y-6 animate-slide-up">
          {/* Success Animation */}
          <div className="text-center space-y-4">
            <div className="w-20 h-20 rounded-full bg-success/10 mx-auto flex items-center justify-center animate-bounce">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-secondary">
              Nice start
            </h2>
            <p className="text-muted-foreground">
              You've analyzed your first meal.
            </p>
            <p className="text-sm text-muted-foreground">
              PlateMind will start learning your eating patterns.
            </p>
          </div>

          {/* Quick Results Summary */}
          <div className="bg-orange-50/50 rounded-2xl p-4 space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Your first meal analysis:</p>
            <div className="flex flex-wrap gap-2">
              {analysisResult.detected_foods?.map((food, index) => (
                <span
                  key={food.food_id || index}
                  className="px-3 py-1 rounded-full bg-white text-secondary text-sm font-medium"
                >
                  {food.name}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2">
              <div className="text-center p-2 bg-white rounded-xl">
                <p className="text-lg font-bold text-primary">{Math.round(analysisResult.total_carbs)}g</p>
                <p className="text-xs text-muted-foreground">Carbs</p>
              </div>
              <div className={`text-center p-2 rounded-xl ${getGIColor(analysisResult.glycemic_impact)}`}>
                <p className="text-lg font-bold">{analysisResult.glycemic_impact}</p>
                <p className="text-xs opacity-80">GI Impact</p>
              </div>
              <div className="text-center p-2 bg-white rounded-xl">
                <p className="text-lg font-bold text-secondary">{Math.round(analysisResult.total_calories)}</p>
                <p className="text-xs text-muted-foreground">Calories</p>
              </div>
            </div>
          </div>

          {/* Lana's Quick Insight */}
          {analysisResult.ai_explanation && (
            <div className="bg-primary/5 rounded-2xl p-4">
              <p className="text-sm font-medium text-primary mb-2">Lana's Insight</p>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {analysisResult.ai_explanation.replace(/\*\*/g, '').replace(/\*/g, '').split('.')[0]}.
              </p>
            </div>
          )}

          {/* Continue Button */}
          <Button
            onClick={handleContinueToDashboard}
            className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold shadow-button-primary"
            data-testid="continue-to-dashboard-btn"
          >
            Continue to Dashboard
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-lg rounded-3xl border border-orange-100/50 shadow-soft-lift p-6 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-4">
            <BarChart3 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-secondary">
            Let's analyze your first meal
          </h1>
          <p className="text-muted-foreground">
            Search for foods you ate and we'll show you how they affect your blood sugar.
          </p>
        </div>

        {/* Food Search Section */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search for a food (e.g., rice, chicken, jollof)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl border-2 border-orange-100 focus:border-primary py-5"
              data-testid="first-meal-search-input"
              autoFocus
            />
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto border border-orange-100 rounded-xl p-2">
              {searchResults.map((food) => {
                const isSelected = selectedFoods.some((f) => f.food_id === food.food_id);
                return (
                  <div
                    key={food.food_id}
                    onClick={() => toggleFoodSelection(food)}
                    className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-primary/10 border-2 border-primary"
                        : "bg-orange-50/50 hover:bg-orange-50 border-2 border-transparent"
                    }`}
                    data-testid={`first-meal-food-item-${food.food_id}`}
                  >
                    <div>
                      <p className="font-medium text-secondary">{food.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {food.carbs_per_100g}g carbs • GI: {food.glycemic_index}
                      </p>
                    </div>
                    {isSelected ? (
                      <CheckCircle className="w-5 h-5 text-primary" />
                    ) : (
                      <Plus className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected Foods */}
          {selectedFoods.length > 0 && (
            <div className="space-y-3 pt-2 border-t border-orange-100">
              <p className="text-sm font-medium text-secondary">
                Selected Foods ({selectedFoods.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedFoods.map((food) => (
                  <span
                    key={food.food_id}
                    onClick={() => toggleFoodSelection(food)}
                    className="px-3 py-2 rounded-full bg-primary text-white text-sm font-medium cursor-pointer flex items-center gap-2 hover:bg-primary/90 transition-colors"
                  >
                    {food.name}
                    <X className="w-3 h-3" />
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Analyze Button */}
        <Button
          onClick={analyzeFirstMeal}
          disabled={loading || selectedFoods.length === 0}
          className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold shadow-button-primary disabled:opacity-50"
          data-testid="analyze-first-meal-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <BarChart3 className="w-5 h-5 mr-2" />
              Analyze My First Meal
            </>
          )}
        </Button>

        {/* Skip Option */}
        <Button
          variant="ghost"
          onClick={handleContinueToDashboard}
          className="w-full text-muted-foreground hover:text-secondary"
          data-testid="skip-first-meal-btn"
        >
          Skip for now
        </Button>
      </Card>
    </div>
  );
};

export default FirstMealScreen;
